import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import {
  Input,
  Output,
  OnInit,
  ViewChild,
  OnDestroy,
  Component,
  ElementRef,
  forwardRef,
  EventEmitter,
  AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { ISnListResponse } from '@lib/api';
import { SnUser } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-chat-user-select',
  templateUrl: './chat-user-select.component.html',
  styleUrls: ['./chat-user-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SnChatUserSelectComponent),
    },
  ],
})
export class SnChatUserSelectComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input() public hasError: boolean;
  @Input() public type = 'direct';
  @Input() public patchedUsers: SnUser[];
  @Input() public exceptionList: string[];

  @Input()
  get value(): string[] {
    return this._value;
  }
  set value(value: string[]) {
    this._value = value;
    this._controlValueAccessorChangeFn(value);
  }

  @Output()
  public readonly usersList = new EventEmitter<string[]>();

  public users: SnUser[];
  public selectedUsers: SnUser[] = [];
  public usersControl = new UntypedFormControl();
  public isLoading: boolean = false;

  @ViewChild('input')
  private readonly _inputRef: ElementRef;

  private _value: string[] = [];
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _usersAPI: SnUsersAPI,
    private readonly _toastrService: ToastrService,
  ) {}

  public ngOnInit(): void {
    this.usersControl.valueChanges
      .pipe(debounceTime(500), takeUntil(this._destroy$))
      .subscribe((value) => {
        this._updateUsers(value);
      });
  }

  public ngAfterViewInit(): void {
    if (this.patchedUsers?.length) {
      this.selectedUsers = this.patchedUsers;
      this.writeValue(this.selectedUsers);
      this._controlValueAccessorTouchedFn();
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackUser(index: number, item: SnUser): any {
    return item.id;
  }

  public addUser(event: SnUser | MatAutocompleteSelectedEvent): void {
    const value = event instanceof MatAutocompleteSelectedEvent ? event.option.value : event;

    const index = this.selectedUsers.indexOf(value);

    if (index === -1) {
      this.selectedUsers.push(value);
      this.writeValue(this.selectedUsers);
      this._controlValueAccessorTouchedFn();
    }

    this._clearInput();
  }

  public removeUser(user: SnUser): void {
    const index = this.selectedUsers.indexOf(user);

    this.selectedUsers.splice(index, 1);
    this.writeValue(this.selectedUsers);
  }

  public writeValue(value: any): void {
    this.value = value?.map((user) => user.id);
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._controlValueAccessorTouchedFn = fn;
  }

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};
  private _controlValueAccessorTouchedFn: () => void = () => {};

  private _updateUsers(value: string): void {
    if (value === '' || !value) {
      this.users = [];
      return;
    }
    this.isLoading = true;
    this._usersAPI
      .getUserList({
        search: value,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (users) => {
          this.isLoading = false;
          this._handleUserList(users);
        },
        error: () => {
          this.isLoading = false;
          this.users = [];
          this._toastrService.error(t('communication.chat.errors.retrieveUsers'));
        },
      });
  }

  private _handleUserList(users: ISnListResponse<SnUser>): void {
    this.users = this.exceptionList
      ? users.results.filter((user) => this.exceptionList.indexOf(user.id) === -1)
      : users.results;
  }

  private _clearInput(): void {
    this._inputRef.nativeElement.value = '';
  }
}
